import { Icon, MuiAlert, StatusChip, strIsNullOrEmpty, Tooltip, Typography } from '@fox/atoms';
import { DATE_FORMAT, OverflowTextCell, ResponsiveTable } from '@fox/data-grid';
import { ResponsiveTableColumn } from '@fox/data-grid/src/responsive-table/contracts';
import { FlexColumnSmallSpaced } from '@fox/layout';
import { useSubscribeTopicWithHandler } from 'fox-extensions';
import moment from 'moment';
import { useState } from 'react';
import { TableWrapper } from '../../../Membership/financial-details/receipts';
import { useGoals } from '../../../Membership/query-hooks/query-implementations';
import { useContentContext } from '../../../content-context';
import { useGoalsContext } from '../../../goals-context';
import { GOALS_DATA_FETCHED } from '../../../topics';
import { GOALS_STATUS, SwiftFoxGoalWithInferredDates } from '../contracts';

export function GoalsTable({
	useLinkedOrganisationData,
	rowsExpandedOnSmallScreens
}: {
	useLinkedOrganisationData: boolean;
	rowsExpandedOnSmallScreens: boolean;
}) {
	const { contentId, requestToken } = useContentContext();
	const { getGoalById, loading, error, getGoalsConfig } = useGoalsContext();
	const [page, setPage] = useState<number>(0);
	const PAGE_SIZE = 10;

	const {
		data: goalsData,
		error: goalsError,
		loading: goalsLoading,
		refetch: goalsRefetch
	} = useGoals({
		contentId,
		requestToken,
		page,
		pageSize: PAGE_SIZE,
		useLinkedOrganisationData,
		triggers: ['page']
	});

	useSubscribeTopicWithHandler(GOALS_DATA_FETCHED, () => goalsRefetch());

	if (!strIsNullOrEmpty(goalsError)) return <MuiAlert severity="error">{goalsError}</MuiAlert>;
	if (!strIsNullOrEmpty(error)) return <MuiAlert severity="error">{error}</MuiAlert>;
	if (loading || goalsLoading) return <></>;

	if (goalsData?.results?.length === 0) return <MuiAlert severity="info">No data to display</MuiAlert>;

	const goalLabel = getGoalsConfig()?.labelOverrides?.Goal || 'Goal';
	const columns: ResponsiveTableColumn<SwiftFoxGoalWithInferredDates>[] = [
		{
			title: 'Created On',
			dataIndex: 'createdOn',
			render: record => {
				return <OverflowTextCell>{moment(record.createdOn).format(DATE_FORMAT)}</OverflowTextCell>;
			}
		},
		{
			title: `${goalLabel}`,
			dataIndex: 'goalId',
			render: record => {
				const goal = getGoalById(record.goalId, useLinkedOrganisationData);
				return <OverflowTextCell>{goal?.name ?? ''}</OverflowTextCell>;
			},
			showOnMobileRow: true,
			mobileHeaderRender: record => {
				const goal = getGoalById(record.goalId, useLinkedOrganisationData);
				return <OverflowTextCell>{goal?.name ?? ''}</OverflowTextCell>;
			}
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: record => <StatusChip maxWidth="15rem" center label={record.status} backgroundColor={GOALS_STATUS[record.status]} />
		},
		{
			title: 'Points',
			dataIndex: 'calculatedPointData',
			render: record => {
				return (
					<Tooltip
						title={
							<div>
								{record.calculatedPointData.pointsInfo.map(
									({ categoryName, catPoints, catPointsRequired, catPointsMax }) => (
										<Typography key={categoryName} variant="body2">
											{categoryName}: {catPoints}
											{catPointsRequired ? `/${catPointsRequired}` : ''}
											{catPointsMax ? ` [Max: ${catPointsMax}]` : ''}
										</Typography>
									)
								)}
							</div>
						}
					>
						<div>
							{record.calculatedPointData.totalPoints}/{record.calculatedPointData.totalPointsRequired}
						</div>
					</Tooltip>
				);
			},
			showOnMobileRow: true,
			mobileHeaderRender: record => {
				return (
					<div>
						{record.calculatedPointData.totalPoints}/{record.calculatedPointData.totalPointsRequired}
					</div>
				);
			},
			mobileRender: record => {
				return (
					<FlexColumnSmallSpaced>
						<div>
							{record.calculatedPointData.pointsInfo.map(({ categoryName, catPoints, catPointsRequired, catPointsMax }) => (
								<Typography key={categoryName} variant="body2">
									{categoryName}: {catPoints}
									{catPointsRequired ? `/${catPointsRequired}` : ''}
									{catPointsMax ? ` [Max: ${catPointsMax}]` : ''}
								</Typography>
							))}
						</div>

						<div>
							{record.calculatedPointData.totalPoints}/{record.calculatedPointData.totalPointsRequired}
						</div>
					</FlexColumnSmallSpaced>
				);
			}
		},
		{
			title: 'Achieved',
			dataIndex: 'goalAchievedInferred',
			render: record => {
				const isAchieved = record.calculatedPointData.goalAchieved;
				return isAchieved ? <Icon sx={{ margin: 'auto' }} iconName={'CheckCircle'} color={'success'} /> : null;
			}
		},
		{
			title: 'Start Date',
			dataIndex: 'startDate',
			render: record => {
				return <OverflowTextCell>{moment(record.startDateInferred).format(DATE_FORMAT)}</OverflowTextCell>;
			}
		},
		{
			title: 'End Date',
			dataIndex: 'endDateInferred',

			render: record => {
				const goal = getGoalById(record.goalId, useLinkedOrganisationData);
				if (!goal || !record.endDateInferred) return <></>;
				return <OverflowTextCell>{moment(record.endDateInferred).format(DATE_FORMAT)}</OverflowTextCell>;
			}
		}
	];

	return (
		<TableWrapper>
			<ResponsiveTable<SwiftFoxGoalWithInferredDates>
				columns={columns}
				data={goalsData?.results}
				loading={goalsLoading}
				rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
				paginationProps={
					goalsData?.total
						? {
								page,
								onPageChange: page => setPage(page),
								total: goalsData.total,
								itemsPerPage: PAGE_SIZE
						  }
						: undefined
				}
			/>
		</TableWrapper>
	);
}
